/**
 * Quarantine Management Page
 * AS9100D-Compliant Nonconforming Material Control
 * 
 * Features:
 * - View and manage quarantined material
 * - Mark items as counterfeit suspect (with regulatory hold)
 * - Request and approve dispositions
 * - Execute dispositions (USE_AS_IS, REPAIR, REWORK, RETURN_SUPPLIER, SCRAP)
 * - Confirm scrap destruction with evidence
 * - Release material back to stock
 * 
 * AS9100D Clauses:
 * - 8.7: Control of Nonconforming Outputs
 * - 8.1.4: Prevention of Counterfeit Parts
 * - 10.2: Nonconformity and Corrective Action
 * 
 * @version 1.0.0
 * @date January 2026
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  ShieldAlert,
  Plus,
  Search,
  Eye,
  Clock,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Loader2,
  RefreshCw,
  Trash2,
  Wrench,
  RotateCcw,
  Undo,
  FileWarning,
  ShieldX,
  ShieldCheck,
  FileSignature,
  Camera,
  Package,
  Building2,
  Hash,
  Calendar,
  ChevronRight,
  Info,
  AlertOctagon,
  ClipboardCheck,
  Send,
  ThumbsUp,
  ThumbsDown,
  Play,
  X,
  Check,
  Lock,
  Unlock,
  FileText,
  ExternalLink
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../components/ui/tabs';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../components/ui/tooltip';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { Separator } from '../components/ui/separator';
import { formatIST } from '../utils/timezone';
import api from '../services/api';
import { toast } from 'sonner';
import ComplianceTooltip from '../components/ComplianceTooltip';
import InventoryLayout from '../components/layouts/InventoryLayout';

// Status badge colors
const QUARANTINE_STATUS_COLORS = {
  PENDING_DISPOSITION: 'bg-amber-100 text-amber-700 border-amber-200',
  AWAITING_MRB: 'bg-purple-100 text-purple-700 border-purple-200',
  DISPOSITION_APPROVED: 'bg-blue-100 text-blue-700 border-blue-200',
  REGULATORY_HOLD: 'bg-red-100 text-red-700 border-red-200',
  CLOSED: 'bg-gray-100 text-gray-600 border-gray-200'
};

const DISPOSITION_STATUS_COLORS = {
  PENDING_APPROVAL: 'bg-amber-100 text-amber-700 border-amber-200',
  APPROVED: 'bg-green-100 text-green-700 border-green-200',
  REJECTED: 'bg-red-100 text-red-700 border-red-200',
  AWAITING_SCRAP_CONFIRMATION: 'bg-orange-100 text-orange-700 border-orange-200',
  EXECUTED: 'bg-gray-100 text-gray-600 border-gray-200'
};

const REASON_LABELS = {
  INSPECTION_FAIL: 'Inspection Failure',
  DAMAGE: 'Damaged Material',
  EXPIRED: 'Expired Material',
  WRONG_PART: 'Wrong Part',
  COUNTERFEIT_SUSPECT: 'Counterfeit Suspect',
  CUSTOMER_RETURN: 'Customer Return',
  PRODUCTION_REJECT: 'Production Reject',
  CYCLE_COUNT_VARIANCE: 'Cycle Count Variance',
  SHELF_LIFE_EXCEEDED: 'Shelf Life Exceeded',
  CONTAMINATION: 'Contamination',
  DOCUMENTATION_ISSUE: 'Documentation Issue'
};

const DISPOSITION_LABELS = {
  USE_AS_IS: 'Use As-Is',
  REPAIR: 'Repair',
  REWORK: 'Rework',
  RETURN_SUPPLIER: 'Return to Supplier',
  SCRAP: 'Scrap'
};

const DISPOSITION_ICONS = {
  USE_AS_IS: CheckCircle,
  REPAIR: Wrench,
  REWORK: RotateCcw,
  RETURN_SUPPLIER: Undo,
  SCRAP: Trash2
};

// Summary Card Component
const SummaryCard = ({ title, value, icon: Icon, color, subtext, onClick }) => (
  <Card 
    className={`cursor-pointer hover:shadow-md transition-all ${onClick ? 'hover:scale-[1.02]' : ''}`}
    onClick={onClick}
  >
    <CardContent className="p-4">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-gray-500">{title}</p>
          <p className={`text-2xl font-bold ${color}`}>{value}</p>
          {subtext && <p className="text-xs text-gray-400 mt-1">{subtext}</p>}
        </div>
        <div className={`p-3 rounded-full ${color.replace('text-', 'bg-').replace('700', '100').replace('600', '100')}`}>
          <Icon className={`h-6 w-6 ${color}`} />
        </div>
      </div>
    </CardContent>
  </Card>
);

export default function QuarantineManagementPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  
  // State
  const [loading, setLoading] = useState(true);
  const [quarantineItems, setQuarantineItems] = useState([]);
  const [summary, setSummary] = useState(null);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [reasonFilter, setReasonFilter] = useState('');
  
  // Selected item
  const [selectedItem, setSelectedItem] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  
  // Dialog states
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showDispositionDialog, setShowDispositionDialog] = useState(false);
  const [showApprovalDialog, setShowApprovalDialog] = useState(false);
  const [showExecuteDialog, setShowExecuteDialog] = useState(false);
  const [showScrapConfirmDialog, setShowScrapConfirmDialog] = useState(false);
  const [showReleaseDialog, setShowReleaseDialog] = useState(false);
  const [showCounterfeitDialog, setShowCounterfeitDialog] = useState(false);
  const [showClearanceDialog, setShowClearanceDialog] = useState(false);
  
  // Form states
  const [createForm, setCreateForm] = useState({
    lot_id: '',
    quantity: '',
    quarantine_reason: '',
    is_counterfeit_suspect: false,
    notes: ''
  });
  
  const [dispositionForm, setDispositionForm] = useState({
    disposition_type: '',
    quantity: '',
    justification: '',
    rma_number: '',
    repair_instructions: ''
  });
  
  const [approvalForm, setApprovalForm] = useState({
    approval_type: '',
    approved: true,
    rejection_reason: '',
    e_signature: ''
  });
  
  const [executeForm, setExecuteForm] = useState({
    execution_notes: '',
    evidence_file: '',
    witness_name: ''
  });
  
  const [scrapForm, setScrapForm] = useState({
    destruction_confirmed: true,
    destruction_method: '',
    additional_notes: ''
  });
  
  const [releaseForm, setReleaseForm] = useState({
    release_reason: '',
    e_signature: ''
  });
  
  const [counterfeitForm, setCounterfeitForm] = useState({
    notes: ''
  });
  
  const [clearanceForm, setClearanceForm] = useState({
    clearance_reference: '',
    clearance_notes: ''
  });
  
  const [submitting, setSubmitting] = useState(false);
  const [selectedDisposition, setSelectedDisposition] = useState(null);
  
  // Available lots for creating quarantine
  const [availableLots, setAvailableLots] = useState([]);
  const [lotsLoading, setLotsLoading] = useState(false);
  
  const pageSize = 20;

  // Load quarantine items
  const loadQuarantineItems = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        page: currentPage,
        page_size: pageSize
      };
      
      if (statusFilter && statusFilter !== 'ALL') params.status = statusFilter;
      if (reasonFilter && reasonFilter !== 'ALL') params.reason = reasonFilter;
      
      const response = await api.get('/api/v1/inventory/v2/quarantine', { params });
      setQuarantineItems(response.data.items || []);
      setTotalCount(response.data.total_count || 0);
    } catch (error) {
      console.error('Error loading quarantine items:', error);
      toast.error('Failed to load quarantine items');
    } finally {
      setLoading(false);
    }
  }, [currentPage, statusFilter, reasonFilter]);

  // Load summary
  const loadSummary = useCallback(async () => {
    try {
      const response = await api.get('/api/v1/inventory/v2/quarantine/summary');
      setSummary(response.data);
    } catch (error) {
      console.error('Error loading summary:', error);
    }
  }, []);

  // Load quarantine detail
  const loadQuarantineDetail = useCallback(async (quarantineId) => {
    setDetailLoading(true);
    try {
      const response = await api.get(`/api/v1/inventory/v2/quarantine/${quarantineId}`);
      setSelectedItem(response.data);
    } catch (error) {
      console.error('Error loading quarantine detail:', error);
      toast.error('Failed to load quarantine details');
    } finally {
      setDetailLoading(false);
    }
  }, []);

  // Load available lots
  const loadAvailableLots = useCallback(async (search = '') => {
    setLotsLoading(true);
    try {
      const response = await api.get('/api/v1/inventory/v2/lots', {
        params: { 
          page: 1, 
          page_size: 50,
          status: 'RELEASED',
          search: search || undefined
        }
      });
      setAvailableLots(response.data.items || []);
    } catch (error) {
      console.error('Error loading lots:', error);
    } finally {
      setLotsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadQuarantineItems();
    loadSummary();
  }, [loadQuarantineItems, loadSummary]);

  // Handle create quarantine
  const handleCreateQuarantine = async () => {
    if (!createForm.lot_id || !createForm.quantity || !createForm.quarantine_reason) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    setSubmitting(true);
    try {
      await api.post('/api/v1/inventory/v2/quarantine', {
        ...createForm,
        quantity: parseFloat(createForm.quantity)
      });
      toast.success('Quarantine record created successfully');
      setShowCreateDialog(false);
      setCreateForm({
        lot_id: '',
        quantity: '',
        quarantine_reason: '',
        is_counterfeit_suspect: false,
        notes: ''
      });
      loadQuarantineItems();
      loadSummary();
    } catch (error) {
      console.error('Error creating quarantine:', error);
      toast.error(error.response?.data?.detail || 'Failed to create quarantine');
    } finally {
      setSubmitting(false);
    }
  };

  // Handle mark counterfeit
  const handleMarkCounterfeit = async () => {
    if (!selectedItem) return;
    
    setSubmitting(true);
    try {
      await api.post(`/api/v1/inventory/v2/quarantine/${selectedItem.quarantine_id}/counterfeit-suspect`, counterfeitForm);
      toast.success('Item marked as counterfeit suspect. NCR created and regulatory hold applied.');
      setShowCounterfeitDialog(false);
      setCounterfeitForm({ notes: '' });
      loadQuarantineDetail(selectedItem.quarantine_id);
      loadSummary();
    } catch (error) {
      console.error('Error marking counterfeit:', error);
      toast.error(error.response?.data?.detail || 'Failed to mark as counterfeit suspect');
    } finally {
      setSubmitting(false);
    }
  };

  // Handle grant clearance
  const handleGrantClearance = async () => {
    if (!selectedItem || !clearanceForm.clearance_reference) {
      toast.error('Clearance reference is required');
      return;
    }
    
    setSubmitting(true);
    try {
      await api.post(`/api/v1/inventory/v2/quarantine/${selectedItem.quarantine_id}/regulatory-clearance`, clearanceForm);
      toast.success('Regulatory clearance granted');
      setShowClearanceDialog(false);
      setClearanceForm({ clearance_reference: '', clearance_notes: '' });
      loadQuarantineDetail(selectedItem.quarantine_id);
      loadSummary();
    } catch (error) {
      console.error('Error granting clearance:', error);
      toast.error(error.response?.data?.detail || 'Failed to grant clearance');
    } finally {
      setSubmitting(false);
    }
  };

  // Handle request disposition
  const handleRequestDisposition = async () => {
    if (!selectedItem || !dispositionForm.disposition_type || !dispositionForm.quantity || !dispositionForm.justification) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    if (dispositionForm.justification.length < 10) {
      toast.error('Justification must be at least 10 characters');
      return;
    }
    
    setSubmitting(true);
    try {
      await api.post(`/api/v1/inventory/v2/quarantine/${selectedItem.quarantine_id}/disposition/request`, {
        ...dispositionForm,
        quantity: parseFloat(dispositionForm.quantity)
      });
      toast.success('Disposition request submitted');
      setShowDispositionDialog(false);
      setDispositionForm({
        disposition_type: '',
        quantity: '',
        justification: '',
        rma_number: '',
        repair_instructions: ''
      });
      loadQuarantineDetail(selectedItem.quarantine_id);
    } catch (error) {
      console.error('Error requesting disposition:', error);
      toast.error(error.response?.data?.detail || 'Failed to request disposition');
    } finally {
      setSubmitting(false);
    }
  };

  // Handle approve disposition
  const handleApproveDisposition = async () => {
    if (!selectedDisposition || !approvalForm.approval_type) {
      toast.error('Please select an approval type');
      return;
    }
    
    if (!approvalForm.approved && !approvalForm.rejection_reason) {
      toast.error('Rejection reason is required');
      return;
    }
    
    setSubmitting(true);
    try {
      await api.post(`/api/v1/inventory/v2/dispositions/${selectedDisposition.disposition_id}/approve`, approvalForm);
      toast.success(approvalForm.approved ? 'Disposition approved' : 'Disposition rejected');
      setShowApprovalDialog(false);
      setApprovalForm({
        approval_type: '',
        approved: true,
        rejection_reason: '',
        e_signature: ''
      });
      loadQuarantineDetail(selectedItem.quarantine_id);
    } catch (error) {
      console.error('Error approving disposition:', error);
      toast.error(error.response?.data?.detail || 'Failed to process approval');
    } finally {
      setSubmitting(false);
    }
  };

  // Handle execute disposition
  const handleExecuteDisposition = async () => {
    if (!selectedDisposition) return;
    
    // For SCRAP, require evidence and witness
    if (selectedDisposition.disposition_type === 'SCRAP') {
      if (!executeForm.evidence_file || !executeForm.witness_name) {
        toast.error('Scrap disposition requires evidence file and witness name');
        return;
      }
    }
    
    setSubmitting(true);
    try {
      await api.post(`/api/v1/inventory/v2/dispositions/${selectedDisposition.disposition_id}/execute`, executeForm);
      toast.success('Disposition executed');
      setShowExecuteDialog(false);
      setExecuteForm({ execution_notes: '', evidence_file: '', witness_name: '' });
      loadQuarantineDetail(selectedItem.quarantine_id);
      loadSummary();
    } catch (error) {
      console.error('Error executing disposition:', error);
      toast.error(error.response?.data?.detail || 'Failed to execute disposition');
    } finally {
      setSubmitting(false);
    }
  };

  // Handle confirm scrap
  const handleConfirmScrap = async () => {
    if (!selectedDisposition) return;
    
    if (!scrapForm.destruction_method) {
      toast.error('Destruction method is required');
      return;
    }
    
    setSubmitting(true);
    try {
      await api.post(`/api/v1/inventory/v2/dispositions/${selectedDisposition.disposition_id}/scrap/confirm`, scrapForm);
      toast.success('Scrap destruction confirmed');
      setShowScrapConfirmDialog(false);
      setScrapForm({ destruction_confirmed: true, destruction_method: '', additional_notes: '' });
      loadQuarantineDetail(selectedItem.quarantine_id);
      loadSummary();
    } catch (error) {
      console.error('Error confirming scrap:', error);
      toast.error(error.response?.data?.detail || 'Failed to confirm scrap');
    } finally {
      setSubmitting(false);
    }
  };

  // Handle release from quarantine
  const handleReleaseFromQuarantine = async () => {
    if (!selectedItem || !releaseForm.release_reason) {
      toast.error('Release reason is required');
      return;
    }
    
    if (releaseForm.release_reason.length < 10) {
      toast.error('Release reason must be at least 10 characters');
      return;
    }
    
    setSubmitting(true);
    try {
      await api.post(`/api/v1/inventory/v2/quarantine/${selectedItem.quarantine_id}/release`, releaseForm);
      toast.success('Material released from quarantine');
      setShowReleaseDialog(false);
      setReleaseForm({ release_reason: '', e_signature: '' });
      setSelectedItem(null);
      loadQuarantineItems();
      loadSummary();
    } catch (error) {
      console.error('Error releasing from quarantine:', error);
      toast.error(error.response?.data?.detail || 'Failed to release from quarantine');
    } finally {
      setSubmitting(false);
    }
  };

  // Get pending approvals for a disposition
  const getPendingApprovals = (disposition) => {
    const pending = [];
    if (disposition.requires_engineering_approval && !disposition.engineering_approved_by) {
      pending.push('engineering');
    }
    if (disposition.requires_qa_approval && !disposition.qa_approved_by) {
      pending.push('qa');
    }
    if (disposition.requires_finance_approval && !disposition.finance_approved_by) {
      pending.push('finance');
    }
    if (disposition.requires_regulatory_approval && !disposition.regulatory_approved_by) {
      pending.push('regulatory');
    }
    return pending;
  };

  // Filter items by search
  const filteredItems = quarantineItems.filter(item => {
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return (
      item.quarantine_number?.toLowerCase().includes(q) ||
      item.lot_number?.toLowerCase().includes(q) ||
      item.hpn_display?.toLowerCase().includes(q)
    );
  });

  const totalPages = Math.ceil(totalCount / pageSize);

  return (
    <InventoryLayout>
      <TooltipProvider>
        <div className="min-h-screen p-6">
          {/* Header */}
          <div className="mb-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-red-100 rounded-lg">
                  <ShieldAlert className="h-6 w-6 text-red-600" />
                </div>
                <div>
                  <h1 className="text-2xl font-bold text-gray-900">Quarantine Management</h1>
                  <p className="text-sm text-gray-500">
                    AS9100D 8.7 - Control of Nonconforming Outputs
                  </p>
                </div>
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={() => {
                    loadQuarantineItems();
                    loadSummary();
                  }}
                >
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Refresh
                </Button>
              <Button 
                onClick={() => {
                  setShowCreateDialog(true);
                  loadAvailableLots();
                }}
              >
                <Plus className="h-4 w-4 mr-2" />
                Create Quarantine
              </Button>
            </div>
          </div>
        </div>

        {/* Summary Cards */}
        {summary && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
            <SummaryCard
              title="Total Open"
              value={summary.total_open || 0}
              icon={ShieldAlert}
              color="text-amber-600"
              subtext="Pending disposition"
            />
            <SummaryCard
              title="Counterfeit Suspects"
              value={summary.counterfeit_suspect_count || 0}
              icon={AlertOctagon}
              color="text-red-600"
              subtext="Critical attention"
              onClick={() => setReasonFilter('COUNTERFEIT_SUSPECT')}
            />
            <SummaryCard
              title="Regulatory Holds"
              value={summary.regulatory_hold_count || 0}
              icon={Lock}
              color="text-purple-600"
              subtext="Awaiting clearance"
            />
            <SummaryCard
              title="Pending Approvals"
              value={summary.pending_dispositions || 0}
              icon={ClipboardCheck}
              color="text-blue-600"
              subtext="Disposition requests"
            />
            <SummaryCard
              title="Closed This Month"
              value={summary.status_counts?.CLOSED || 0}
              icon={CheckCircle}
              color="text-green-600"
              subtext="Resolved"
            />
          </div>
        )}

        {/* Main Content */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Quarantine List */}
          <div className="lg:col-span-2">
            <Card>
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg">Quarantine Items</CardTitle>
                  <div className="flex gap-2">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                      <Input
                        placeholder="Search..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="pl-9 w-48"
                      />
                    </div>
                    <Select value={statusFilter} onValueChange={setStatusFilter}>
                      <SelectTrigger className="w-40">
                        <SelectValue placeholder="All Status" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="ALL">All Status</SelectItem>
                        <SelectItem value="PENDING_DISPOSITION">Pending Disposition</SelectItem>
                        <SelectItem value="AWAITING_MRB">Awaiting MRB</SelectItem>
                        <SelectItem value="DISPOSITION_APPROVED">Approved</SelectItem>
                        <SelectItem value="REGULATORY_HOLD">Regulatory Hold</SelectItem>
                        <SelectItem value="CLOSED">Closed</SelectItem>
                      </SelectContent>
                    </Select>
                    <Select value={reasonFilter} onValueChange={setReasonFilter}>
                      <SelectTrigger className="w-40">
                        <SelectValue placeholder="All Reasons" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="ALL">All Reasons</SelectItem>
                        {Object.entries(REASON_LABELS).map(([key, label]) => (
                          <SelectItem key={key} value={key}>{label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
                  </div>
                ) : filteredItems.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <ShieldAlert className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                    <p>No quarantine items found</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {filteredItems.map((item) => (
                      <div
                        key={item.quarantine_id}
                        className={`p-4 border rounded-lg cursor-pointer transition-all hover:shadow-md ${
                          selectedItem?.quarantine_id === item.quarantine_id
                            ? 'border-red-300 bg-red-50'
                            : 'border-gray-200 hover:border-gray-300'
                        } ${item.is_counterfeit_suspect ? 'border-l-4 border-l-red-500' : ''}`}
                        onClick={() => loadQuarantineDetail(item.quarantine_id)}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <span className="font-medium text-gray-900">
                                {item.quarantine_number}
                              </span>
                              <Badge className={QUARANTINE_STATUS_COLORS[item.status]}>
                                {item.status?.replace(/_/g, ' ')}
                              </Badge>
                              {item.is_counterfeit_suspect && (
                                <Badge className="bg-red-500 text-white">
                                  <AlertOctagon className="h-3 w-3 mr-1" />
                                  COUNTERFEIT SUSPECT
                                </Badge>
                              )}
                              {item.regulatory_hold && !item.regulatory_clearance && (
                                <Badge variant="outline" className="border-purple-500 text-purple-600">
                                  <Lock className="h-3 w-3 mr-1" />
                                  Regulatory Hold
                                </Badge>
                              )}
                            </div>
                            <div className="text-sm text-gray-600 space-y-1">
                              <div className="flex items-center gap-4">
                                <span className="flex items-center gap-1">
                                  <Package className="h-3 w-3" />
                                  {item.hpn_display || 'N/A'}
                                </span>
                                <span className="flex items-center gap-1">
                                  <Hash className="h-3 w-3" />
                                  Lot: {item.lot_number || 'N/A'}
                                </span>
                                <span className="font-medium">
                                  Qty: {item.quantity}
                                </span>
                              </div>
                              <div className="flex items-center gap-4 text-gray-500">
                                <span className="flex items-center gap-1">
                                  <FileWarning className="h-3 w-3" />
                                  {REASON_LABELS[item.quarantine_reason] || item.quarantine_reason}
                                </span>
                                <span className="flex items-center gap-1">
                                  <Clock className="h-3 w-3" />
                                  {item.days_in_quarantine || 0} days
                                </span>
                              </div>
                            </div>
                          </div>
                          <ChevronRight className="h-5 w-5 text-gray-400" />
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="flex items-center justify-between mt-4 pt-4 border-t">
                    <span className="text-sm text-gray-500">
                      Showing {(currentPage - 1) * pageSize + 1} to {Math.min(currentPage * pageSize, totalCount)} of {totalCount}
                    </span>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={currentPage === 1}
                        onClick={() => setCurrentPage(p => p - 1)}
                      >
                        Previous
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={currentPage === totalPages}
                        onClick={() => setCurrentPage(p => p + 1)}
                      >
                        Next
                      </Button>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Detail Panel */}
          <div className="lg:col-span-1">
            {selectedItem ? (
              <Card className="sticky top-6">
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-lg">Quarantine Details</CardTitle>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setSelectedItem(null)}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  {detailLoading ? (
                    <div className="flex items-center justify-center py-8">
                      <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
                    </div>
                  ) : (
                    <>
                      {/* Header Info */}
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <span className="text-lg font-semibold">{selectedItem.quarantine_number}</span>
                          <Badge className={QUARANTINE_STATUS_COLORS[selectedItem.status]}>
                            {selectedItem.status?.replace(/_/g, ' ')}
                          </Badge>
                        </div>
                        
                        {selectedItem.is_counterfeit_suspect && (
                          <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                            <div className="flex items-center gap-2 text-red-700 font-medium">
                              <AlertOctagon className="h-5 w-5" />
                              COUNTERFEIT SUSPECT
                            </div>
                            <p className="text-sm text-red-600 mt-1">
                              This item is under regulatory hold. Disposition requires regulatory clearance.
                            </p>
                            {selectedItem.ncr_number && (
                              <p className="text-sm mt-1">
                                NCR: <span className="font-medium">{selectedItem.ncr_number}</span>
                              </p>
                            )}
                          </div>
                        )}
                      </div>

                      <Separator />

                      {/* Material Info */}
                      <div className="space-y-2">
                        <h4 className="text-sm font-medium text-gray-700 flex items-center gap-2">
                          <Package className="h-4 w-4" />
                          Material
                        </h4>
                        <div className="grid grid-cols-2 gap-2 text-sm">
                          <div>
                            <span className="text-gray-500">Part:</span>
                            <p className="font-medium">{selectedItem.hpn_display || 'N/A'}</p>
                          </div>
                          <div>
                            <span className="text-gray-500">Lot:</span>
                            <p className="font-medium">{selectedItem.lot_number || 'N/A'}</p>
                          </div>
                          <div>
                            <span className="text-gray-500">Quantity:</span>
                            <p className="font-medium">{selectedItem.quantity}</p>
                          </div>
                          <div>
                            <span className="text-gray-500">Days in QRN:</span>
                            <p className="font-medium">{selectedItem.days_in_quarantine || 0} days</p>
                          </div>
                        </div>
                      </div>

                      <Separator />

                      {/* Reason */}
                      <div className="space-y-2">
                        <h4 className="text-sm font-medium text-gray-700 flex items-center gap-2">
                          <FileWarning className="h-4 w-4" />
                          Quarantine Reason
                          <ComplianceTooltip tooltipId="quarantine.status" size="sm" />
                        </h4>
                        <div className="p-2 bg-gray-50 rounded-lg">
                          <span className="font-medium">
                            {REASON_LABELS[selectedItem.quarantine_reason] || selectedItem.quarantine_reason}
                          </span>
                          {selectedItem.notes && (
                            <p className="text-sm text-gray-600 mt-1">{selectedItem.notes}</p>
                          )}
                        </div>
                      </div>

                      {/* Regulatory Clearance Status */}
                      {selectedItem.regulatory_hold && (
                        <>
                          <Separator />
                          <div className="space-y-2">
                            <h4 className="text-sm font-medium text-gray-700 flex items-center gap-2">
                              {selectedItem.regulatory_clearance ? (
                                <Unlock className="h-4 w-4 text-green-600" />
                              ) : (
                                <Lock className="h-4 w-4 text-purple-600" />
                              )}
                              Regulatory Status
                              <ComplianceTooltip tooltipId="quarantine.regulatory_hold" size="sm" />
                            </h4>
                            {selectedItem.regulatory_clearance ? (
                              <div className="p-2 bg-green-50 border border-green-200 rounded-lg">
                                <div className="flex items-center gap-2 text-green-700 font-medium">
                                  <ShieldCheck className="h-4 w-4" />
                                  Clearance Granted
                                </div>
                                <p className="text-sm mt-1">Ref: {selectedItem.regulatory_clearance_ref}</p>
                              </div>
                            ) : (
                              <div className="p-2 bg-purple-50 border border-purple-200 rounded-lg">
                                <div className="flex items-center gap-2 text-purple-700 font-medium">
                                  <Lock className="h-4 w-4" />
                                  Regulatory Hold Active
                                </div>
                                <p className="text-sm text-purple-600 mt-1">
                                  Clearance required before disposition
                                </p>
                              </div>
                            )}
                          </div>
                        </>
                      )}

                      {/* Dispositions */}
                      {selectedItem.dispositions && selectedItem.dispositions.length > 0 && (
                        <>
                          <Separator />
                          <div className="space-y-2">
                            <h4 className="text-sm font-medium text-gray-700 flex items-center gap-2">
                              <ClipboardCheck className="h-4 w-4" />
                              Dispositions
                              <ComplianceTooltip tooltipId="quarantine.disposition_type" size="sm" />
                            </h4>
                            <div className="space-y-2">
                              {selectedItem.dispositions.map((disp) => {
                                const Icon = DISPOSITION_ICONS[disp.disposition_type] || FileText;
                                const pendingApprovals = getPendingApprovals(disp);
                                
                                return (
                                  <div key={disp.disposition_id} className="p-3 border rounded-lg">
                                    <div className="flex items-center justify-between mb-2">
                                      <div className="flex items-center gap-2">
                                        <Icon className="h-4 w-4 text-gray-500" />
                                        <span className="font-medium">
                                          {DISPOSITION_LABELS[disp.disposition_type] || disp.disposition_type}
                                        </span>
                                      </div>
                                      <Badge className={DISPOSITION_STATUS_COLORS[disp.status]}>
                                        {disp.status?.replace(/_/g, ' ')}
                                      </Badge>
                                    </div>
                                    <div className="text-sm text-gray-600">
                                      <p>Qty: {disp.quantity}</p>
                                      <p className="truncate">Justification: {disp.justification}</p>
                                    </div>
                                    
                                    {/* Pending Approvals */}
                                    {disp.status === 'PENDING_APPROVAL' && pendingApprovals.length > 0 && (
                                      <div className="mt-2 flex flex-wrap gap-1">
                                        {pendingApprovals.map(type => (
                                          <Badge key={type} variant="outline" className="text-xs">
                                            Needs: {type}
                                          </Badge>
                                        ))}
                                      </div>
                                    )}
                                    
                                    {/* Action Buttons */}
                                    <div className="mt-2 flex gap-1">
                                      {disp.status === 'PENDING_APPROVAL' && (
                                        <Button
                                          size="sm"
                                          variant="outline"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            setSelectedDisposition(disp);
                                            setShowApprovalDialog(true);
                                          }}
                                        >
                                          <ThumbsUp className="h-3 w-3 mr-1" />
                                          Review
                                        </Button>
                                      )}
                                      {disp.status === 'APPROVED' && (
                                        <Button
                                          size="sm"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            setSelectedDisposition(disp);
                                            setShowExecuteDialog(true);
                                          }}
                                        >
                                          <Play className="h-3 w-3 mr-1" />
                                          Execute
                                        </Button>
                                      )}
                                      {disp.status === 'AWAITING_SCRAP_CONFIRMATION' && (
                                        <Button
                                          size="sm"
                                          variant="destructive"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            setSelectedDisposition(disp);
                                            setShowScrapConfirmDialog(true);
                                          }}
                                        >
                                          <Trash2 className="h-3 w-3 mr-1" />
                                          Confirm Destruction
                                        </Button>
                                      )}
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        </>
                      )}

                      <Separator />

                      {/* Actions */}
                      <div className="space-y-2">
                        <h4 className="text-sm font-medium text-gray-700">Actions</h4>
                        <div className="grid grid-cols-2 gap-2">
                          {!selectedItem.is_counterfeit_suspect && selectedItem.status !== 'CLOSED' && (
                            <Button
                              variant="outline"
                              size="sm"
                              className="text-red-600 border-red-300 hover:bg-red-50"
                              onClick={() => setShowCounterfeitDialog(true)}
                            >
                              <AlertOctagon className="h-4 w-4 mr-1" />
                              Mark Counterfeit
                            </Button>
                          )}
                          
                          {selectedItem.regulatory_hold && !selectedItem.regulatory_clearance && (
                            <Button
                              variant="outline"
                              size="sm"
                              className="text-purple-600 border-purple-300 hover:bg-purple-50"
                              onClick={() => setShowClearanceDialog(true)}
                            >
                              <Unlock className="h-4 w-4 mr-1" />
                              Grant Clearance
                            </Button>
                          )}
                          
                          {selectedItem.status !== 'CLOSED' && 
                           !selectedItem.regulatory_hold && (
                            <>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => {
                                  setDispositionForm({
                                    ...dispositionForm,
                                    quantity: selectedItem.quantity?.toString() || ''
                                  });
                                  setShowDispositionDialog(true);
                                }}
                              >
                                <Send className="h-4 w-4 mr-1" />
                                Request Disposition
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                className="text-green-600 border-green-300 hover:bg-green-50"
                                onClick={() => setShowReleaseDialog(true)}
                              >
                                <ShieldCheck className="h-4 w-4 mr-1" />
                                Release
                              </Button>
                            </>
                          )}
                          
                          {selectedItem.status !== 'CLOSED' && 
                           selectedItem.regulatory_hold && 
                           selectedItem.regulatory_clearance && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                setDispositionForm({
                                  ...dispositionForm,
                                  quantity: selectedItem.quantity?.toString() || ''
                                });
                                setShowDispositionDialog(true);
                              }}
                            >
                              <Send className="h-4 w-4 mr-1" />
                              Request Disposition
                            </Button>
                          )}
                        </div>
                      </div>

                      {/* Audit Info */}
                      <Separator />
                      <div className="text-xs text-gray-500 space-y-1">
                        <p>Created: {selectedItem.quarantined_at ? formatIST(selectedItem.quarantined_at) : 'N/A'}</p>
                        {selectedItem.closed_at && (
                          <p>Closed: {formatIST(selectedItem.closed_at)}</p>
                        )}
                      </div>
                    </>
                  )}
                </CardContent>
              </Card>
            ) : (
              <Card className="sticky top-6">
                <CardContent className="py-12 text-center text-gray-500">
                  <ShieldAlert className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                  <p>Select a quarantine item to view details</p>
                </CardContent>
              </Card>
            )}
          </div>
        </div>

        {/* Create Quarantine Dialog */}
        <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <ShieldAlert className="h-5 w-5 text-red-600" />
                Create Quarantine Record
              </DialogTitle>
              <DialogDescription>
                Quarantine material for investigation per AS9100D 8.7
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>Lot *</Label>
                <Select
                  value={createForm.lot_id}
                  onValueChange={(v) => {
                    const lot = availableLots.find(l => l.lot_id === v);
                    setCreateForm({
                      ...createForm,
                      lot_id: v,
                      quantity: lot?.current_quantity?.toString() || ''
                    });
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select a lot" />
                  </SelectTrigger>
                  <SelectContent>
                    {availableLots.map(lot => (
                      <SelectItem key={lot.lot_id} value={lot.lot_id}>
                        {lot.lot_number} - {lot.hpn_display} (Qty: {lot.current_quantity})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label>Quantity *</Label>
                <Input
                  type="number"
                  value={createForm.quantity}
                  onChange={(e) => setCreateForm({ ...createForm, quantity: e.target.value })}
                  placeholder="Enter quantity"
                />
              </div>
              
              <div>
                <Label>Quarantine Reason *</Label>
                <Select
                  value={createForm.quarantine_reason}
                  onValueChange={(v) => setCreateForm({ ...createForm, quarantine_reason: v })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select reason" />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(REASON_LABELS).map(([key, label]) => (
                      <SelectItem key={key} value={key}>{label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="counterfeit"
                  checked={createForm.is_counterfeit_suspect}
                  onChange={(e) => setCreateForm({ ...createForm, is_counterfeit_suspect: e.target.checked })}
                  className="rounded border-gray-300"
                />
                <Label htmlFor="counterfeit" className="flex items-center gap-2">
                  <AlertOctagon className="h-4 w-4 text-red-500" />
                  Mark as Counterfeit Suspect
                </Label>
              </div>
              
              <div>
                <Label>Notes</Label>
                <Textarea
                  value={createForm.notes}
                  onChange={(e) => setCreateForm({ ...createForm, notes: e.target.value })}
                  placeholder="Additional notes..."
                  rows={3}
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowCreateDialog(false)}>
                Cancel
              </Button>
              <Button onClick={handleCreateQuarantine} disabled={submitting}>
                {submitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                Create Quarantine
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Mark Counterfeit Dialog */}
        <AlertDialog open={showCounterfeitDialog} onOpenChange={setShowCounterfeitDialog}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle className="flex items-center gap-2 text-red-600">
                <AlertOctagon className="h-5 w-5" />
                Mark as Counterfeit Suspect
              </AlertDialogTitle>
              <AlertDialogDescription className="space-y-2">
                <p>This action will:</p>
                <ul className="list-disc list-inside text-sm space-y-1">
                  <li>Apply a <strong>regulatory hold</strong> on the material</li>
                  <li>Create a <strong>critical severity NCR</strong></li>
                  <li>Block all disposition until regulatory clearance</li>
                  <li>Create a <strong>permanent audit record</strong></li>
                </ul>
                <p className="text-red-600 font-medium mt-2">This action cannot be undone.</p>
              </AlertDialogDescription>
            </AlertDialogHeader>
            <div className="py-2">
              <Label>Notes (optional)</Label>
              <Textarea
                value={counterfeitForm.notes}
                onChange={(e) => setCounterfeitForm({ notes: e.target.value })}
                placeholder="Describe why this is suspected counterfeit..."
                rows={3}
              />
            </div>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                className="bg-red-600 hover:bg-red-700"
                onClick={handleMarkCounterfeit}
                disabled={submitting}
              >
                {submitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                Confirm Counterfeit Suspect
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Grant Clearance Dialog */}
        <Dialog open={showClearanceDialog} onOpenChange={setShowClearanceDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Unlock className="h-5 w-5 text-purple-600" />
                Grant Regulatory Clearance
              </DialogTitle>
              <DialogDescription>
                Grant clearance to allow disposition of counterfeit suspect material per AS9100D 8.1.4
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>Clearance Reference *</Label>
                <Input
                  value={clearanceForm.clearance_reference}
                  onChange={(e) => setClearanceForm({ ...clearanceForm, clearance_reference: e.target.value })}
                  placeholder="Enter clearance document reference"
                />
              </div>
              <div>
                <Label>Notes</Label>
                <Textarea
                  value={clearanceForm.clearance_notes}
                  onChange={(e) => setClearanceForm({ ...clearanceForm, clearance_notes: e.target.value })}
                  placeholder="Additional clearance notes..."
                  rows={3}
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowClearanceDialog(false)}>
                Cancel
              </Button>
              <Button onClick={handleGrantClearance} disabled={submitting}>
                {submitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                Grant Clearance
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Request Disposition Dialog */}
        <Dialog open={showDispositionDialog} onOpenChange={setShowDispositionDialog}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <ClipboardCheck className="h-5 w-5 text-blue-600" />
                Request Disposition
                <ComplianceTooltip tooltipId="quarantine.disposition_type" size="sm" />
              </DialogTitle>
              <DialogDescription>
                Request disposition decision for quarantined material per AS9100D 8.7
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>Disposition Type *</Label>
                <Select
                  value={dispositionForm.disposition_type}
                  onValueChange={(v) => setDispositionForm({ ...dispositionForm, disposition_type: v })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select disposition type" />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(DISPOSITION_LABELS).map(([key, label]) => {
                      const Icon = DISPOSITION_ICONS[key];
                      return (
                        <SelectItem key={key} value={key}>
                          <div className="flex items-center gap-2">
                            <Icon className="h-4 w-4" />
                            {label}
                          </div>
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
                
                {/* Approval requirements info */}
                {dispositionForm.disposition_type && (
                  <div className="mt-2 p-2 bg-blue-50 rounded-lg text-sm text-blue-700">
                    <p className="font-medium">Approval Requirements:</p>
                    <ul className="list-disc list-inside">
                      <li>QA Approval (always required)</li>
                      {dispositionForm.disposition_type === 'USE_AS_IS' && (
                        <li>Engineering Approval</li>
                      )}
                      {dispositionForm.disposition_type === 'SCRAP' && (
                        <li>Finance Approval</li>
                      )}
                      {selectedItem?.is_counterfeit_suspect && (
                        <li>Regulatory Approval</li>
                      )}
                    </ul>
                  </div>
                )}
              </div>
              
              <div>
                <Label>Quantity *</Label>
                <Input
                  type="number"
                  value={dispositionForm.quantity}
                  onChange={(e) => setDispositionForm({ ...dispositionForm, quantity: e.target.value })}
                  placeholder="Enter quantity"
                />
              </div>
              
              <div>
                <Label>Justification * (min 10 characters)</Label>
                <Textarea
                  value={dispositionForm.justification}
                  onChange={(e) => setDispositionForm({ ...dispositionForm, justification: e.target.value })}
                  placeholder="Provide detailed justification for the disposition decision..."
                  rows={4}
                />
              </div>
              
              {dispositionForm.disposition_type === 'RETURN_SUPPLIER' && (
                <div>
                  <Label>RMA Number</Label>
                  <Input
                    value={dispositionForm.rma_number}
                    onChange={(e) => setDispositionForm({ ...dispositionForm, rma_number: e.target.value })}
                    placeholder="Enter RMA number"
                  />
                </div>
              )}
              
              {(dispositionForm.disposition_type === 'REPAIR' || dispositionForm.disposition_type === 'REWORK') && (
                <div>
                  <Label>Repair/Rework Instructions</Label>
                  <Textarea
                    value={dispositionForm.repair_instructions}
                    onChange={(e) => setDispositionForm({ ...dispositionForm, repair_instructions: e.target.value })}
                    placeholder="Enter repair or rework instructions..."
                    rows={3}
                  />
                </div>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowDispositionDialog(false)}>
                Cancel
              </Button>
              <Button onClick={handleRequestDisposition} disabled={submitting}>
                {submitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                Submit Request
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Approval Dialog */}
        <Dialog open={showApprovalDialog} onOpenChange={setShowApprovalDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <ThumbsUp className="h-5 w-5 text-green-600" />
                Review Disposition Request
              </DialogTitle>
              <DialogDescription>
                Approve or reject disposition request per AS9100D 8.7
              </DialogDescription>
            </DialogHeader>
            {selectedDisposition && (
              <div className="space-y-4">
                {/* Disposition Summary */}
                <div className="p-3 bg-gray-50 rounded-lg space-y-2">
                  <div className="flex items-center gap-2">
                    {(() => {
                      const Icon = DISPOSITION_ICONS[selectedDisposition.disposition_type];
                      return Icon ? <Icon className="h-4 w-4" /> : null;
                    })()}
                    <span className="font-medium">
                      {DISPOSITION_LABELS[selectedDisposition.disposition_type]}
                    </span>
                  </div>
                  <p className="text-sm">Quantity: {selectedDisposition.quantity}</p>
                  <p className="text-sm">Justification: {selectedDisposition.justification}</p>
                </div>
                
                <div>
                  <Label>Approval Type *</Label>
                  <Select
                    value={approvalForm.approval_type}
                    onValueChange={(v) => setApprovalForm({ ...approvalForm, approval_type: v })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select your approval type" />
                    </SelectTrigger>
                    <SelectContent>
                      {getPendingApprovals(selectedDisposition).map(type => (
                        <SelectItem key={type} value={type}>
                          {type.charAt(0).toUpperCase() + type.slice(1)} Approval
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="flex items-center gap-4">
                  <Label>Decision:</Label>
                  <div className="flex gap-2">
                    <Button
                      variant={approvalForm.approved ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setApprovalForm({ ...approvalForm, approved: true })}
                    >
                      <ThumbsUp className="h-4 w-4 mr-1" />
                      Approve
                    </Button>
                    <Button
                      variant={!approvalForm.approved ? 'destructive' : 'outline'}
                      size="sm"
                      onClick={() => setApprovalForm({ ...approvalForm, approved: false })}
                    >
                      <ThumbsDown className="h-4 w-4 mr-1" />
                      Reject
                    </Button>
                  </div>
                </div>
                
                {!approvalForm.approved && (
                  <div>
                    <Label>Rejection Reason *</Label>
                    <Textarea
                      value={approvalForm.rejection_reason}
                      onChange={(e) => setApprovalForm({ ...approvalForm, rejection_reason: e.target.value })}
                      placeholder="Provide reason for rejection..."
                      rows={3}
                    />
                  </div>
                )}
                
                <div>
                  <Label>E-Signature (optional)</Label>
                  <Input
                    value={approvalForm.e_signature}
                    onChange={(e) => setApprovalForm({ ...approvalForm, e_signature: e.target.value })}
                    placeholder="Enter your signature..."
                  />
                </div>
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowApprovalDialog(false)}>
                Cancel
              </Button>
              <Button 
                onClick={handleApproveDisposition} 
                disabled={submitting}
                variant={approvalForm.approved ? 'default' : 'destructive'}
              >
                {submitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                {approvalForm.approved ? 'Approve' : 'Reject'} Disposition
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Execute Dialog */}
        <Dialog open={showExecuteDialog} onOpenChange={setShowExecuteDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Play className="h-5 w-5 text-green-600" />
                Execute Disposition
              </DialogTitle>
              <DialogDescription>
                Execute the approved disposition action
              </DialogDescription>
            </DialogHeader>
            {selectedDisposition && (
              <div className="space-y-4">
                <div className="p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center gap-2 font-medium">
                    {(() => {
                      const Icon = DISPOSITION_ICONS[selectedDisposition.disposition_type];
                      return Icon ? <Icon className="h-4 w-4" /> : null;
                    })()}
                    {DISPOSITION_LABELS[selectedDisposition.disposition_type]}
                  </div>
                  <p className="text-sm mt-1">Quantity: {selectedDisposition.quantity}</p>
                </div>
                
                <div>
                  <Label>Execution Notes</Label>
                  <Textarea
                    value={executeForm.execution_notes}
                    onChange={(e) => setExecuteForm({ ...executeForm, execution_notes: e.target.value })}
                    placeholder="Additional notes about the execution..."
                    rows={3}
                  />
                </div>
                
                {selectedDisposition.disposition_type === 'SCRAP' && (
                  <>
                    <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                      <p className="text-sm text-red-700 font-medium flex items-center gap-2">
                        <AlertTriangle className="h-4 w-4" />
                        Scrap requires evidence and witness per AS9100D 8.7
                      </p>
                    </div>
                    <div>
                      <Label>Evidence File URL *</Label>
                      <Input
                        value={executeForm.evidence_file}
                        onChange={(e) => setExecuteForm({ ...executeForm, evidence_file: e.target.value })}
                        placeholder="Enter URL to destruction photo"
                      />
                    </div>
                    <div>
                      <Label>Witness Name *</Label>
                      <Input
                        value={executeForm.witness_name}
                        onChange={(e) => setExecuteForm({ ...executeForm, witness_name: e.target.value })}
                        placeholder="Enter witness name"
                      />
                    </div>
                  </>
                )}
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowExecuteDialog(false)}>
                Cancel
              </Button>
              <Button onClick={handleExecuteDisposition} disabled={submitting}>
                {submitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                Execute Disposition
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Scrap Confirmation Dialog */}
        <AlertDialog open={showScrapConfirmDialog} onOpenChange={setShowScrapConfirmDialog}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle className="flex items-center gap-2 text-red-600">
                <Trash2 className="h-5 w-5" />
                Confirm Scrap Destruction
                <ComplianceTooltip tooltipId="quarantine.scrap_confirmation" size="sm" />
              </AlertDialogTitle>
              <AlertDialogDescription>
                Confirm that material has been physically destroyed and rendered unusable per AS9100D 8.7.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <div className="space-y-4 py-2">
              <div>
                <Label>Destruction Method *</Label>
                <Select
                  value={scrapForm.destruction_method}
                  onValueChange={(v) => setScrapForm({ ...scrapForm, destruction_method: v })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select destruction method" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="SHREDDING">Shredding</SelectItem>
                    <SelectItem value="CRUSHING">Crushing</SelectItem>
                    <SelectItem value="CUTTING">Cutting/Dismantling</SelectItem>
                    <SelectItem value="CHEMICAL">Chemical Treatment</SelectItem>
                    <SelectItem value="INCINERATION">Incineration</SelectItem>
                    <SelectItem value="OTHER">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label>Additional Notes</Label>
                <Textarea
                  value={scrapForm.additional_notes}
                  onChange={(e) => setScrapForm({ ...scrapForm, additional_notes: e.target.value })}
                  placeholder="Any additional destruction notes..."
                  rows={2}
                />
              </div>
              
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="destruction_confirmed"
                  checked={scrapForm.destruction_confirmed}
                  onChange={(e) => setScrapForm({ ...scrapForm, destruction_confirmed: e.target.checked })}
                  className="rounded border-gray-300"
                />
                <Label htmlFor="destruction_confirmed" className="text-sm">
                  I confirm that the material has been physically destroyed and rendered unusable
                </Label>
              </div>
            </div>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                className="bg-red-600 hover:bg-red-700"
                onClick={handleConfirmScrap}
                disabled={submitting || !scrapForm.destruction_confirmed}
              >
                {submitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                Confirm Destruction
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Release Dialog */}
        <Dialog open={showReleaseDialog} onOpenChange={setShowReleaseDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <ShieldCheck className="h-5 w-5 text-green-600" />
                Release from Quarantine
              </DialogTitle>
              <DialogDescription>
                Release material back to available stock. Use when investigation shows no issue.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>Release Reason * (min 10 characters)</Label>
                <Textarea
                  value={releaseForm.release_reason}
                  onChange={(e) => setReleaseForm({ ...releaseForm, release_reason: e.target.value })}
                  placeholder="Explain why this material is being released from quarantine..."
                  rows={4}
                />
              </div>
              <div>
                <Label>E-Signature (optional)</Label>
                <Input
                  value={releaseForm.e_signature}
                  onChange={(e) => setReleaseForm({ ...releaseForm, e_signature: e.target.value })}
                  placeholder="Enter your signature..."
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowReleaseDialog(false)}>
                Cancel
              </Button>
              <Button 
                onClick={handleReleaseFromQuarantine} 
                disabled={submitting}
                className="bg-green-600 hover:bg-green-700"
              >
                {submitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                Release to Stock
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        </div>
      </TooltipProvider>
    </InventoryLayout>
  );
}
